import { useReportBug } from "@/hooks/mutations/useReportBug";
import { useAuth } from "@/hooks/useAuth";
import { useState } from "react";
import toast from "react-hot-toast";
import {
  FaBug,
  FaLightbulb,
  FaTimes,
  FaSpinner,
  FaCheck,
} from "react-icons/fa";
import { HiOutlineSelector } from "react-icons/hi";

const BugSuggestionForm = () => {
  const { user } = useAuth();
  const userId = user?.id ? Number(user.id) : null;

  const [bug, setBug] = useState("");
  const [reportType, setReportType] = useState("all");
  const [suggestion, setSuggestion] = useState("");
  const [bugModel, setBugModel] = useState(true);

  const { mutateAsync, isPending } = useReportBug();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!bug && !suggestion) {
      toast.error("Please fill in at least a bug or suggestion!");
      return;
    }

    let title = "Bug & Suggestion Report";
    let description = "";

    if (reportType === "bug") {
      title = "Bug Report";
      description = bug;
    } else if (reportType === "suggestion") {
      title = "Suggestion Report";
      description = suggestion;
    } else {
      description = `[Bug]: ${bug}\n\n[Suggestion]: ${suggestion}`;
    }

    const payload = {
      reportedByUserId: userId,
      title,
      description,
      severity: null,
    };

    try {
      await mutateAsync(payload);
      setBug("");
      setSuggestion("");
      toast.success("Report submitted successfully!");
    } catch (error) {
      console.error(error);
    }
  };

  const handleModel = () => {
    setBugModel((prev) => !prev);
  };

  if (!bugModel) return null;

  return (
    <div className="animate-in fade-in fixed inset-0 z-50 flex items-center justify-center bg-wh ite/10 p-4 backdrop-blur-md">
      <div
        className="relative w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with gradient */}
        <div className="bg-linear-to-r from-blue-600 to-sky-500 px-6 py-4 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold">MUCIIFI KENYA</h2>
              <p className="text-xs text-blue-100">
                Report a Bug or Suggest an Improvement
              </p>
            </div>
            <button
              onClick={handleModel}
              className="rounded-full p-1.5 text-white/70 transition-colors hover:bg-white/20 hover:text-white"
              disabled={isPending}
            >
              <FaTimes size={18} />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5 p-6">
          {/* Report Type Selector */}
          <div>
            <label
              htmlFor="report-type"
              className="mb-1 block text-sm font-medium text-gray-700"
            >
              Report Type
            </label>
            <div className="relative">
              <select
                id="report-type"
                className="w-full appearance-none rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5 pr-10 text-sm text-gray-800 transition focus:border-blue-400 focus:ring-2 focus:ring-blue-200 focus:outline-none"
                value={reportType}
                onChange={(e) => setReportType(e.target.value)}
              >
                <option value="all">Bug & Suggestion</option>
                <option value="bug">Bug</option>
                <option value="suggestion">Suggestion</option>
              </select>
              <HiOutlineSelector
                className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-gray-400"
                size={18}
              />
            </div>
          </div>

          {/* Bug Textarea */}
          {reportType !== "suggestion" && (
            <div>
              <label
                htmlFor="bug"
                className="mb-1 block text-sm font-medium text-gray-700"
              >
                Bug Details
              </label>
              <div className="relative">
                <FaBug
                  className="absolute top-3.5 left-3 text-gray-400"
                  size={16}
                />
                <textarea
                  id="bug"
                  value={bug}
                  onChange={(e) => setBug(e.target.value)}
                  placeholder="Describe the issue you encountered..."
                  className="min-h-20 w-full resize-y rounded-xl border border-gray-200 bg-gray-50 py-2.5 pr-4 pl-10 text-sm text-gray-800 placeholder-gray-400 transition focus:border-blue-400 focus:ring-2 focus:ring-blue-200 focus:outline-none"
                />
              </div>
            </div>
          )}

          {/* Suggestion Textarea */}
          {reportType !== "bug" && (
            <div>
              <label
                htmlFor="suggestion"
                className="mb-1 block text-sm font-medium text-gray-700"
              >
                Suggestion Details
              </label>
              <div className="relative">
                <FaLightbulb
                  className="absolute top-3.5 left-3 text-gray-400"
                  size={16}
                />
                <textarea
                  id="suggestion"
                  value={suggestion}
                  onChange={(e) => setSuggestion(e.target.value)}
                  placeholder="We highly value your ideas..."
                  className="min-h-20 w-full resize-y rounded-xl border border-gray-200 bg-gray-50 py-2.5 pr-4 pl-10 text-sm text-gray-800 placeholder-gray-400 transition focus:border-blue-400 focus:ring-2 focus:ring-blue-200 focus:outline-none"
                />
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={handleModel}
              className="w-full rounded-xl border border-gray-300 bg-white px-5 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-50 hover:shadow-sm active:scale-[0.98] sm:w-auto"
              disabled={isPending}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="group relative w-full overflow-hidden rounded bg-linear-to-r from-blue-600 to-sky-500 px-6 py-2.5 text-sm font-semibold text-white shadow-md transition hover:scale-[1.02] hover:shadow-lg active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto"
              disabled={isPending}
            >
              <span className="relative flex items-center justify-center gap-2">
                {isPending ? (
                  <>
                    <FaSpinner className="animate-spin" size={16} />
                    Submitting...
                  </>
                ) : (
                  <>
                    <FaCheck size={14} />
                    Submit Report
                  </>
                )}
              </span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BugSuggestionForm;
